import React, { useState, useMemo } from 'react'
import { newUserInvite, validateUserInvite } from '@paramount-prestart/shared'
import { createEmployee } from 'services/clients.svc'
import { useNavigate } from 'react-router-dom'
import { useMutation } from '@tanstack/react-query'
import { toast } from 'react-toastify'
import { IconHelmet } from '@tabler/icons-react'
import './AddSupervisor.scss'
import {
  PLACEHOLDER_EMAIL,
  PLACEHOLDER_FIRSTNAME,
  PLACEHOLDER_PHONE,
  PLACEHOLDER_SURNAME,
  PLACEHOLDER_CONTACT_NAME,
} from '@paramount-prestart/shared/placeholders'

export default function AddSupervisor({ companyId }) {
  const [values, setValues] = useState({
    ...newUserInvite(),
    emergencyContactName: null,
    emergencyContactPhone: null,
  })
  const navigate = useNavigate()
  const errors = useMemo(() => validateUserInvite(values), [values])

  const canSubmit = useMemo(
    () =>
      Object.values(values).findIndex((x) => x === null) === -1
        ? !errors || Object.keys(errors).length < 1
        : false,
    [values, errors],
  )

  const handleInputChange = (e) => {
    const { name, value } = e.target
    setValues((v) => ({ ...v, [name]: value }))
  }

  const addSupervisorMutation = useMutation(
    () =>
      createEmployee(companyId, {
        firstName: values.firstName,
        surname: values.surname,
        email: values.contactEmail,
        phone: values.phone,
        emergencyContactName: values.emergencyContactName,
        emergencyContactPhone: values.emergencyContactPhone,
      }),
    {
      onSuccess: () => {
        toast.success('Supervisor added successfully')
        navigate(-1)
      },
      onError: (err) => {
        toast.error(err.message || 'Failed to add supervisor')
        console.error('Failed to add supervisor', err)
      },
    },
  )

  return (
    <div className="add-supervisor">
      <div className="as-card">
        <div className="as-header">
          <IconHelmet size={18} stroke={1.5} />
          Add Site Supervisor
        </div>
        <p className="as-description">
          The supervisor will receive a welcome email with access instructions.
        </p>
        <div className="as-grid">
          <div className="as-field">
            <label htmlFor="as-firstName">First Name *</label>
            <input
              id="as-firstName"
              type="text"
              name="firstName"
              tabIndex={1}
              autoComplete="off"
              value={values.firstName || ''}
              aria-invalid={values.firstName !== null ? Boolean(errors.firstName) : 'none'}
              placeholder={PLACEHOLDER_FIRSTNAME}
              onChange={handleInputChange}
            />
            {values.firstName && errors?.firstName && <small>{errors.firstName}</small>}
          </div>
          <div className="as-field">
            <label htmlFor="as-surname">Surname *</label>
            <input
              id="as-surname"
              type="text"
              name="surname"
              tabIndex={2}
              autoComplete="off"
              value={values.surname || ''}
              aria-invalid={values.surname !== null ? Boolean(errors.surname) : 'none'}
              placeholder={PLACEHOLDER_SURNAME}
              onChange={handleInputChange}
            />
            {values.surname && errors?.surname && <small>{errors.surname}</small>}
          </div>
          <div className="as-field">
            <label htmlFor="as-email">Email Address *</label>
            <input
              id="as-email"
              type="text"
              name="contactEmail"
              tabIndex={3}
              autoComplete="off"
              value={values.contactEmail || ''}
              aria-invalid={values.contactEmail !== null ? Boolean(errors.contactEmail) : 'none'}
              placeholder={PLACEHOLDER_EMAIL}
              onChange={handleInputChange}
            />
            {values.contactEmail && errors?.contactEmail && <small>{errors.contactEmail}</small>}
          </div>
          <div className="as-field">
            <label htmlFor="as-phone">Phone Number *</label>
            <input
              id="as-phone"
              type="text"
              name="phone"
              tabIndex={4}
              autoComplete="off"
              value={values.phone || ''}
              aria-invalid={values.phone !== null ? Boolean(errors.phone) : 'none'}
              placeholder={PLACEHOLDER_PHONE}
              onChange={handleInputChange}
            />
            {values.phone && errors?.phone && <small>{errors.phone}</small>}
          </div>
          <div className="as-field">
            <label htmlFor="as-emergencyName">Emergency Contact Name</label>
            <input
              id="as-emergencyName"
              type="text"
              name="emergencyContactName"
              tabIndex={5}
              autoComplete="off"
              value={values.emergencyContactName || ''}
              placeholder={PLACEHOLDER_CONTACT_NAME}
              onChange={handleInputChange}
            />
          </div>
          <div className="as-field">
            <label htmlFor="as-emergencyPhone">Emergency Contact Number</label>
            <input
              id="as-emergencyPhone"
              type="text"
              name="emergencyContactPhone"
              tabIndex={6}
              autoComplete="off"
              value={values.emergencyContactPhone || ''}
              placeholder={PLACEHOLDER_PHONE}
              onChange={handleInputChange}
            />
          </div>
        </div>
        <button
          className="as-submit-btn"
          type="button"
          onClick={() => addSupervisorMutation.mutate()}
          disabled={!canSubmit || addSupervisorMutation.isLoading}
          aria-busy={addSupervisorMutation.isLoading}
        >
          <IconHelmet size={15} stroke={2} />
          {addSupervisorMutation.isLoading ? 'Please wait…' : 'Add & Notify'}
        </button>
      </div>
    </div>
  )
}
